import { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Linkedin, Send } from 'lucide-react'

interface HomeLayoutProps {
  children: ReactNode
}

export default function HomeLayout({ children }: HomeLayoutProps) {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="relative flex flex-col min-h-screen overflow-hidden bg-gray-900 scroll-smooth">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute rounded-full -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/30 to-blue-200/30 blur-3xl animate-pulse-slow"></div>
        <div
          className="absolute rounded-full -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-200/30 to-orange-200/30 blur-3xl animate-pulse-slow"
          style={{ animationDelay: '1s' }}
        ></div>
        <div className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-orange-100/20 to-blue-100/20 blur-3xl animate-float"></div>
      </div>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="px-4 py-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 group">
              <img src="/icon.svg" alt="Icon" className="w-8 h-8 sm:w-10 sm:h-10" />
              <span className="text-lg font-bold text-white sm:text-xl">PexiBlock</span>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="items-center hidden space-x-6 md:flex">
              <Link to="/" className="font-medium text-gray-300 transition-colors hover:text-white">
                Home
              </Link>
              <Link
                to="/about"
                className="font-medium text-gray-300 transition-all duration-300 hover:text-white hover:scale-105"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="font-medium text-gray-300 transition-all duration-300 hover:text-white hover:scale-105"
              >
                Contact
              </Link>
              <Link
                to="/faq"
                className="font-medium text-gray-300 transition-all duration-300 hover:text-white hover:scale-105"
              >
                FAQ
              </Link>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="px-4 py-2 font-semibold text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
                >
                  Dashboard
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="px-3 py-2 text-sm font-semibold text-white transition-colors bg-orange-500 rounded-lg sm:px-4 sm:text-base hover:bg-orange-600"
                >
                  Get Started
                </Link>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="p-2 text-white md:hidden hover:text-orange-400 focus:outline-none"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-4 pt-2 pb-4 space-y-1 transition-all duration-300 ease-in-out transform bg-slate-900/95 backdrop-blur-md animate-in slide-in-from-top-2">
                <Link 
                  to="/" 
                  className="block px-3 py-2 font-medium text-gray-300 transition-all duration-200 rounded-md hover:text-white hover:bg-slate-800 hover:translate-x-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link 
                  to="/about" 
                  className="block px-3 py-2 font-medium text-gray-300 transition-all duration-200 rounded-md hover:text-white hover:bg-slate-800 hover:translate-x-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="block px-3 py-2 font-medium text-gray-300 transition-all duration-200 rounded-md hover:text-white hover:bg-slate-800 hover:translate-x-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                <Link 
                  to="/faq" 
                  className="block px-3 py-2 font-medium text-gray-300 transition-all duration-200 rounded-md hover:text-white hover:bg-slate-800 hover:translate-x-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 font-medium text-white transition-all duration-200 bg-orange-500 rounded-md hover:bg-orange-600 hover:translate-x-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="block px-3 py-2 font-medium text-white transition-all duration-200 bg-orange-500 rounded-md hover:bg-orange-600 hover:translate-x-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 pt-16 bg-gray-900 sm:pt-20">{children}</main>

      {/* Footer */}
      <footer className="relative z-10 border-t bg-slate-900 border-white/20">
        <div className="container px-4 py-12 mx-auto">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4 space-x-2">
                <Link to="/" className="flex items-center space-x-3 group">
                  <div className="">
                    <Link to="/" className="flex items-center space-x-2 group">
                      <img src="/icon.svg" alt="Icon" className="w-10 h-10" />
                      <span className="text-xl font-bold text-white">PexiBlock</span>
                    </Link>
                  </div>
                </Link>
              </div>
              <p className="max-w-md mb-6 text-sm text-gray-300">
                Our platform is built for businesses that demand reliability and protection. With no chargebacks, merchants can accept payments confidently, free from the risks and disputes common in traditional payment systems.
              </p>
              <div className="flex items-center space-x-4">
                <img src="/PCI-DSS-1.png" alt="Visa" className="h-10" /> | <span><img src="/security.png" alt="security-metrics" className="h-16" /></span>
              </div>
            </div>
            {/* <div>
              <h3 className="mb-4 font-semibold text-white">Payment Solutions</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>
                  <a href="#features" className="transition-colors hover:text-orange-400">
                    Card-to-Crypto Payments
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="transition-colors hover:text-orange-400">
                    Instant Settlements
                  </a>
                </li>
                <li>
                  <a href="#" className="transition-colors hover:text-orange-400">
                    API Documentation
                  </a>
                </li>
                <li>
                  <a href="#" className="transition-colors hover:text-orange-400">
                    Integration Guide
                  </a>
                </li>
                <li>
                  <a href="#" className="transition-colors hover:text-orange-400">
                    Security Features
                  </a>
                </li>
              </ul>
            </div> */}
            <div>
              <h3 className="mb-4 font-semibold text-pexi-orange">Company</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>
                  <Link to="/about" className="transition-colors hover:text-orange-400">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="transition-colors hover:text-orange-400">
                    Terms & Conditions
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="mb-4 font-semibold text-pexi-orange">Support</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>
                  <Link to="/contact" className="transition-colors hover:text-orange-400">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="transition-colors hover:text-orange-400">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/help-center" className="transition-colors hover:text-orange-400">
                    Help Center
                  </Link>
                </li>
                <li>
                  <a href="/developer-community" className="transition-colors hover:text-orange-400">
                    Developer Community
                  </a>
                </li>
                <li>
                  <Link to="/privacy" className="transition-colors hover:text-orange-400">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 mt-8 border-t border-white/20">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <p className="text-sm text-gray-300">© 2025 PexiBlocks. All rights reserved.</p>
              <div className="flex mt-4 space-x-4 md:mt-0">
                <a
                  href="https://linkedin.com/company/pexilabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 text-gray-400 transition-colors bg-gray-700 rounded-full hover:bg-orange-500 hover:text-white"
                  aria-label="Follow us on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://t.me/pexilabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 text-gray-400 transition-colors bg-gray-700 rounded-full hover:bg-orange-500 hover:text-white"
                  aria-label="Join us on Telegram"
                >
                  <Send className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
